import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from 'react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { AppLayoutContext } from '../../../providers/AppLayout'
import { useAppNavigate } from '../../../utils/url'
import { APP_UPDATE_NOTICE, SnackbarContext } from '../../../providers/SnackbarContext'
import { formatDate } from '../../../utils/formatting'

import { AppPermissions } from '../../../models/AppPermissions.model'
import { ConditionalStatus } from '../../../models/ConditionalStatus.model'
import { ConditionalStatusRelations } from '../../../models/ConditionalStatusRelations.model'

import UserService from '../../../services/UserService/UserService'
import { ConditionalStatusService } from '../../../services/ConditionalStatusService/ConditionalStatusService'

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import Loading from '../../../components/Loading/Loading.component'
import Breadcrumbs from '../../../components/Breadcrumbs/Breadcrumbs.component'
import ConditionalStatusForm from '../shared/ConditionalStatusForm.component'
import FormDialog from '../../../components/FormDialog/FormDialog.component'
import { AppStateContext } from '../../../providers/AppStateContext'
import { TrailStatuses } from '../../../models/TrailStatuses.model'
import { startCase } from 'lodash'
import DetailSection from '../../../components/Sections/DetailSection.component'
import DetailHeader from '../../../components/Sections/DetailHeader.component'
import { useEditSectionParam } from '../../../components/Sections/useEditSectionParam'
import { DetailField, DetailFieldGrid } from '../../../components/Sections/DetailField.component'
import { SectionDefinition } from '../../../components/Sections/Sections.types'
import { ConditionalStatusSectionIds, conditionalStatusSections } from '../shared/ConditionalStatusSections'

const ConditionalStatusDetail = (): JSX.Element => {
  const navigate = useAppNavigate()
  const { id } = useParams<{ id: string }>()
  const [loadingError, setLoadingError] = useState('')

  const { user } = useAuth0()
  const hasWritePermissions = UserService.hasPermissions(user, [AppPermissions.editTrailFinder])

  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const { isEditing, editSection, openEditor, closeEditor } = useEditSectionParam()
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false)

  const conditionalStatusService = useMemo(() => new ConditionalStatusService(), []) // memo to prevent re-creating service on every render

  useEffect(() => {
    AppLayoutContext.setPageName(`Conditional Status Detail`)
  }, [id])

  const queryClient = useQueryClient()
  const { isLoading: isLoadingConditionalStatus, data: conditionalStatus } = useQuery(
    [
      'ConditionalStatusService.get',
      {
        id: Number(id),
        relations: [ConditionalStatusRelations.conditionalStatusGroup],
      },
      AppStateContext.getRegion(),
    ],
    () =>
      conditionalStatusService.get({
        id: Number(id),
        relations: [ConditionalStatusRelations.conditionalStatusGroup],
      }),
    {
      onError: (err) => {
        setLoadingError(typeof err === 'string' ? err : 'Failed to load conditional status.')
        console.error(err)
      },
    },
  )

  // consolidate the various loading states into one
  useEffect(() => {
    setIsLoading(isLoadingConditionalStatus)
  }, [isLoadingConditionalStatus])

  const handleEdit = () => {
    closeEditor()
  }

  const handleDeleteClick = () => {
    // show a confirmation dialog before deleting
    setIsDeleteConfirmationOpen(true)
  }

  const handleDelete = () => {
    if (conditionalStatus?.id) {
      setIsSaving(true)
      setIsDeleteConfirmationOpen(false)
      conditionalStatusService
        .delete(conditionalStatus.id)
        .then(() => {
          SnackbarContext.show(
            `Conditional Status deleted successfully. You will now be redirected.`,
            'success',
            APP_UPDATE_NOTICE,
          )

          // after a delay of 1 second, redirect to the conditionalStatus list page
          setTimeout(() => {
            navigate(`/conditional-status-groups/${conditionalStatus?.conditionalStatusGroup?.id}`)
            queryClient.removeQueries([
              'ConditionalStatusService.get',
              {
                id: Number(id),
                relations: [ConditionalStatusRelations.conditionalStatusGroup],
              },
            ])
            // invalidate all queries to ensure any queries that are using the modified record are updated
            queryClient.invalidateQueries()
          }, 1000)
        })
        .catch((err) => {
          console.error(err)
          SnackbarContext.show(`Conditional Status failed to delete: ${err}`, 'error')
          setIsSaving(false)
        })
    }
  }

  const sectionById = (sectionId: ConditionalStatusSectionIds) =>
    conditionalStatusSections.find((section) => section.id === sectionId) as SectionDefinition
  // only offer per-section edit shortcuts to users who can edit
  const editSectionHandler = (sectionId: ConditionalStatusSectionIds) =>
    hasWritePermissions ? () => openEditor(sectionId) : undefined
  const statusLabel = conditionalStatus
    ? startCase(TrailStatuses[conditionalStatus.status]) || conditionalStatus.status
    : ''
  const withUnit = (value: number | null | undefined, unit: string) =>
    value === null || value === undefined || String(value) === '' ? '-' : `${value} ${unit}`

  return (
    <>
      {conditionalStatus && (
        <FormDialog<ConditionalStatus>
          open={isEditing}
          entity={conditionalStatus}
          entityLabel="Conditional Status"
          onClose={closeEditor}
        >
          <ConditionalStatusForm
            conditionalStatus={conditionalStatus}
            initialSection={editSection}
            onEdit={handleEdit}
            onCancel={closeEditor}
          />
        </FormDialog>
      )}
      <Dialog
        fullWidth
        maxWidth="xs"
        open={isDeleteConfirmationOpen}
        onClose={() => setIsDeleteConfirmationOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent dividers>Are you sure you want to delete this conditional status?</DialogContent>
        <DialogActions>
          <Button autoFocus onClick={() => setIsDeleteConfirmationOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleDelete}>Confirm</Button>
        </DialogActions>
      </Dialog>
      <Paper sx={{ p: 2 }}>
        {isLoading && !loadingError && <Loading sx={{ py: 20 }} />}
        {loadingError && (
          <Stack direction="row" justifyContent="center" alignItems="center" sx={{ py: 20 }}>
            <Typography variant="error">{loadingError}</Typography>
          </Stack>
        )}
        {!isLoading && !loadingError && conditionalStatus && (
          <>
            {isSaving && <Loading sx={{ py: 20 }} />}
            {!isSaving && (
              <>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                  <Breadcrumbs
                    items={() => {
                      const breadcrumbs: Array<{ label: string; href?: string }> = [
                        {
                          label: 'Conditional Status Groups',
                          href: '/conditional-status-groups',
                        },
                      ]
                      if (conditionalStatus.conditionalStatusGroup?.id) {
                        breadcrumbs.push({
                          label: conditionalStatus.conditionalStatusGroup.name,
                          href: `/conditional-status-groups/${conditionalStatus.conditionalStatusGroup.id}`,
                        })
                      }
                      breadcrumbs.push({ label: `Conditional Status ID: ${conditionalStatus.id}` })

                      return breadcrumbs
                    }}
                  />
                  {hasWritePermissions && (
                    <Stack direction="row" justifyContent="end" alignItems="center" spacing={2}>
                      <Button variant="text" onClick={handleDeleteClick}>
                        Delete
                      </Button>
                      <Button variant="contained" onClick={() => openEditor()}>
                        Edit
                      </Button>
                    </Stack>
                  )}
                </Stack>
                <DetailHeader
                  title={statusLabel}
                  subtitle={
                    <>
                      {conditionalStatus.conditionalStatusGroup?.id && (
                        <>
                          <Link href={`/conditional-status-groups/${conditionalStatus.conditionalStatusGroup.id}`}>
                            {conditionalStatus.conditionalStatusGroup.name}
                          </Link>
                          {' · '}
                        </>
                      )}
                      {conditionalStatus.region} · ID {conditionalStatus.id}
                    </>
                  }
                  meta={[
                    `Updated ${formatDate(conditionalStatus.updatedAt)}`,
                    `Created ${formatDate(conditionalStatus.createdAt)}`,
                  ]}
                />

                <Grid container spacing={2}>
                  {/* --- General ------------------------------------------------- */}
                  <Grid item zero={12} md={6}>
                    <DetailSection
                      section={sectionById(ConditionalStatusSectionIds.general)}
                      onEdit={editSectionHandler(ConditionalStatusSectionIds.general)}
                    >
                      <DetailField label="Conditional Status Group">
                        {conditionalStatus.conditionalStatusGroup?.id ? (
                          <Link href={`/conditional-status-groups/${conditionalStatus.conditionalStatusGroup.id}`}>
                            {conditionalStatus.conditionalStatusGroup.name}
                          </Link>
                        ) : (
                          '-'
                        )}
                      </DetailField>
                      <DetailFieldGrid>
                        <DetailField label="Status">{statusLabel}</DetailField>
                        <DetailField label="Sort Weight">
                          {conditionalStatus.sortWeight ?? '-'}
                          <Typography variant="body2" color="grey.500" component="span" sx={{ width: '100%' }}>
                            Lower numbers are matched before higher numbers
                          </Typography>
                        </DetailField>
                      </DetailFieldGrid>
                    </DetailSection>
                  </Grid>

                  {/* --- Conditions ---------------------------------------------- */}
                  <Grid item zero={12} md={6}>
                    <DetailSection
                      section={sectionById(ConditionalStatusSectionIds.conditions)}
                      onEdit={editSectionHandler(ConditionalStatusSectionIds.conditions)}
                    >
                      <DetailFieldGrid minColumnWidth={140}>
                        <DetailField label="Precip Min">{withUnit(conditionalStatus.precipMin, 'mm')}</DetailField>
                        <DetailField label="Precip Max">{withUnit(conditionalStatus.precipMax, 'mm')}</DetailField>
                        <DetailField label="Lookback Hrs Min">
                          {withUnit(conditionalStatus.lookbackHoursMin, 'hrs')}
                        </DetailField>
                        <DetailField label="Lookback Hrs Max">
                          {withUnit(conditionalStatus.lookbackHoursMax, 'hrs')}
                        </DetailField>
                      </DetailFieldGrid>
                    </DetailSection>
                  </Grid>
                </Grid>
              </>
            )}
          </>
        )}
      </Paper>
    </>
  )
}
export default ConditionalStatusDetail
